import React, { useEffect, useRef, useCallback, Suspense, lazy, Component } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/toaster';
import QuickSearch from './components/layout/QuickSearch';
import OverflowTooltip from './components/layout/OverflowTooltip';
import { useSessionTimeout } from './hooks/useSessionTimeout';
import SessionTimeoutModal from './components/SessionTimeoutModal';
import api from './config/api';
import MaintenanceScreen from './components/MaintenanceScreen';
import { subscribe as subscribeMaintenance, isDown } from './config/maintenance';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error) {
    // Chunk loading failures (e.g. stale cache after deploy) → hard reload
    if (
      error?.name === 'ChunkLoadError' ||
      error?.message?.includes('Loading chunk') ||
      error?.message?.includes('dynamically imported module')
    ) {
      window.location.reload();
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-8 max-w-md w-full text-center">
            <p className="text-lg font-semibold text-gray-800 mb-2">Something went wrong</p>
            <p className="text-sm text-gray-500 mb-4">
              {this.state.error?.message || 'An unexpected error occurred.'}
            </p>
            <button
              className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm hover:bg-orange-600 transition-colors"
              onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }}
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Login/Register loaded eagerly — these are entry-point pages that
// must be available immediately (no lazy loading). Everything else is code-split.
import Login from './pages/Login';
import Register from './pages/Register';
import MicrosoftCallback from './pages/MicrosoftCallback';
import GuestAccessPage from './pages/GuestAccessPage';
import DepartmentPrompt from './components/DepartmentPrompt';
import FileEditorPage from './pages/FileEditorPage';
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const WorkspacesHome = lazy(() => import('./pages/WorkspacesHome'));
const AllBoards = lazy(() => import('./pages/AllBoards'));
const NewBoard = lazy(() => import('./pages/NewBoard'));
const BoardPage = lazy(() => import('./pages/BoardPage'));
const DashboardsPage = lazy(() => import('./pages/DashboardsPage'));
const NewWorkspace = lazy(() => import('./pages/NewWorkspace'));
const AutomationsPage = lazy(() => import('./pages/AutomationsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AIAgentsPage = lazy(() => import('./pages/AIAgentsPage'));
const AIAgentCreation = lazy(() => import('./pages/AIAgentCreation'));
const TeamPage = lazy(() => import('./pages/TeamPage'));
const CompanySelection = lazy(() => import('./pages/CompanySelection'));
const JoinTeamPage = lazy(() => import('./pages/JoinTeamPage'));
const PublicFormPage = lazy(() => import('./pages/PublicFormPage'));

// Protected Route Component — manages session timeout and activity heartbeat
const ProtectedRoute = ({ children }) => {
  const { user, loading, logout, sessionId } = useAuth();

  // Stable reference so useSessionTimeout's effect doesn't re-run every render
  const handleTimeout = useCallback(() => { logout(); }, [logout]);

  // ── Activity heartbeat (inlined to avoid separate-file import issues) ──────
  // Sends a heartbeat every 60 s ONLY when the user was active in that window.
  // Idle minutes are never counted.
  const wasActiveRef = useRef(false);
  useEffect(() => {
    if (!sessionId) return;
    const onActivity = () => { wasActiveRef.current = true; };
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    const interval = setInterval(async () => {
      if (!wasActiveRef.current) return;
      wasActiveRef.current = false;
      try { await api.post(`/sessions/heartbeat/${sessionId}`); } catch { /* silent */ }
    }, 60000);
    return () => {
      clearInterval(interval);
      events.forEach(e => window.removeEventListener(e, onActivity));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Signed out after 3 hours with no activity in ANY AcuityFlo tab (was 30
  // minutes — too short for people reading boards or working in another tab).
  // The login token itself still expires after 8 hours (backend auth.py).
  const { showWarning, secondsLeft, stayLoggedIn } = useSessionTimeout({
    timeoutMinutes: 180,
    onTimeout: handleTimeout,
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      {children}
      <DepartmentPrompt />
      {showWarning && (
        <SessionTimeoutModal
          secondsLeft={secondsLeft}
          warningSeconds={120}
          onStayLoggedIn={stayLoggedIn}
          onLogout={logout}
        />
      )}
    </>
  );
};

// Shows the maintenance page whenever the backend is confirmed down; children
// (the whole app) render normally otherwise. Recovers automatically via the
// /health poll in config/maintenance.js.
function MaintenanceGate({ children }) {
  const [down, setDown] = React.useState(isDown());
  React.useEffect(() => subscribeMaintenance(setDown), []);
  if (down) return <MaintenanceScreen />;
  return children;
}

function App() {
  return (
    <div className="App">
      <MaintenanceGate>
      <BrowserRouter>
        <ThemeProvider>
        <AuthProvider>
          <WorkspaceProvider>
            <ErrorBoundary>
            <Suspense fallback={
              <div className="min-h-screen flex items-center justify-center">
                <div className="text-gray-500">Loading…</div>
              </div>
            }>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/auth/microsoft" element={<MicrosoftCallback />} />
              <Route path="/guest-access" element={<GuestAccessPage />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/join/:teamSlug" element={<JoinTeamPage />} />
              <Route path="/f/:formId" element={<PublicFormPage />} />
              <Route
                path="/files/:fileId/edit"
                element={
                  <ProtectedRoute>
                    <FileEditorPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/select-company"
                element={
                  <ProtectedRoute>
                    <CompanySelection />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/workspaces"
                element={
                  <ProtectedRoute>
                    <WorkspacesHome />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/boards"
                element={
                  <ProtectedRoute>
                    <AllBoards />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/team"
                element={
                  <ProtectedRoute>
                    <TeamPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/workspaces/new"
                element={
                  <ProtectedRoute>
                    <NewWorkspace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/workspaces/:workspaceId/dashboards"
                element={
                  <ProtectedRoute>
                    <DashboardsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/workspaces/:workspaceId/automations"
                element={
                  <ProtectedRoute>
                    <AutomationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/boards/new"
                element={
                  <ProtectedRoute>
                    <NewBoard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/boards/:boardId"
                element={
                  <ProtectedRoute>
                    <BoardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/boards/:boardId/settings"
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ai-agents"
                element={
                  <ProtectedRoute>
                    <AIAgentsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ai-agents/new"
                element={
                  <ProtectedRoute>
                    <AIAgentCreation />
                  </ProtectedRoute>
                }
              />
              <Route path="/" element={<Navigate to="/workspaces" replace />} />
            </Routes>
            </Suspense>
            </ErrorBoundary>
            <Toaster />
            <QuickSearch />
            <OverflowTooltip />
          </WorkspaceProvider>
        </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
      </MaintenanceGate>
    </div>
  );
}

export default App;
